import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import { AlertTriangle, AlertCircle, LogOut, Accessibility } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import AxioCard from "../../axio-ds/Card/AxioCard";
import { IconCircle, getIconCircleColor, IconCircleVariant } from "./IconCircle";
import { ModerationFlag } from "./types";
import { colors, radii, spacing } from "../../../styles/design-system";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmails(value: string): string | null {
  if (!value.trim()) return null;
  const emails = value
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
  const invalid = emails.find((e) => !EMAIL_REGEX.test(e));
  return invalid ? `"${invalid}" is not a valid email address` : null;
}

export interface ModerationFlagCardProps {
  flag: ModerationFlag;
  onToggle: () => void;
  onEmailsChange: (emails: string) => void;
}

function ModerationFlagCard({ flag, onToggle, onEmailsChange }: ModerationFlagCardProps) {
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailBlur = () => {
    setEmailError(validateEmails(flag.emails));
  };

  const getFlagVariant = (iconType: ModerationFlag["icon"]): IconCircleVariant => {
    switch (iconType) {
      case "sexual":
      case "selfHarm":
        return "purple-muted";
      case "hate":
      case "violence":
        return "error-muted";
      case "harassment":
        return "orange-muted";
      case "withdrawal":
        return "warning-muted";
      case "ada":
        return "info-muted";
    }
  };

  const getFlagIconElement = (iconType: ModerationFlag["icon"]) => {
    const iconSize = 20;
    const variant = getFlagVariant(iconType);
    const iconColor = getIconCircleColor(variant);

    switch (iconType) {
      case "sexual":
      case "hate":
      case "harassment":
      case "violence":
        return <AlertTriangle size={iconSize} color={iconColor} />;
      case "selfHarm":
        return <AlertCircle size={iconSize} color={iconColor} />;
      case "withdrawal":
        return <LogOut size={iconSize} color={iconColor} />;
      case "ada":
        return <Accessibility size={iconSize} color={iconColor} />;
    }
  };

  return (
    <AxioCard variant="basic" disabled={!flag.enabled} style={[styles.card, !flag.enabled && styles.cardDisabled]}>
      <View style={styles.cardContent}>
        {/* Icon */}
        <IconCircle variant={getFlagVariant(flag.icon)} rounded="lg" icon={getFlagIconElement(flag.icon)} />

        {/* Content */}
        <View style={styles.textContainer}>
          <View style={styles.titleRow}>
            <CustomText text={flag.name} size="xl" weight="bold" useTranslationText={false} style={styles.title} />
            <View style={styles.idBadge}>
              <CustomText text={flag.id} size="xs" useTranslationText={false} style={styles.idText} />
            </View>
          </View>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={flag.description} size="s" useTranslationText={false} style={styles.description} />
          <CustomSpacing type="vertical" size="s" />
          <CustomTextInput
            value={flag.emails}
            onChangeText={(val) => {
              setEmailError(null);
              onEmailsChange(val);
            }}
            onBlur={handleEmailBlur}
            placeholder="Enter emails separated by commas (e.g., admin@school.edu, safety@school.edu)"
            accessibilityLabel={`Email recipients for ${flag.name}`}
            multiline={false}
            editable={flag.enabled}
            style={!flag.enabled ? styles.inputDisabled : undefined}
          />
          {emailError && (
            <CustomText text={emailError} size="xs" useTranslationText={false} style={styles.emailError} />
          )}
        </View>

        {/* Toggle */}
        <CustomSwitch
          value={flag.enabled}
          onValueChange={onToggle}
          accessibilityLabel={`Toggle ${flag.name}`}
          size="m"
        />
      </View>
    </AxioCard>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radii.xl,
    zIndex: 1,
    overflow: "visible",
  },
  cardDisabled: {
    opacity: 0.75,
  },
  cardContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
    overflow: "visible",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  title: {
    color: colors.grey[900],
  },
  idBadge: {
    backgroundColor: colors.grey[100],
    borderRadius: radii.xs,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  idText: {
    color: colors.grey[500],
    fontFamily: "monospace",
  },
  description: {
    color: colors.grey[600],
  },
  inputDisabled: {
    opacity: 0.5,
  },
  emailError: {
    color: "#EF4444",
    marginTop: 4,
  },
});

export default ModerationFlagCard;
